import { useMemo, useState } from 'react'
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Stack, TextField, Tooltip, Typography, alpha,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import SensorsOutlinedIcon from '@mui/icons-material/SensorsOutlined'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined'
import DevicesOtherOutlinedIcon from '@mui/icons-material/DevicesOtherOutlined'
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { IotSnapshot } from '@cmms/api-client'
import { ApiError } from '@cmms/api-client'
import type { AssetSoftPoint } from '@cmms/types'

import { api } from '../../../lib/api'
import { toast } from '../../../store/snackbarStore'
import { useConfirm } from '../../../components/ConfirmDialog'
import { useOrgSettings } from '../../../hooks/useOrgSettings'
import { usePermissions } from '../../../hooks/usePermissions'

interface Props {
  assetId: string
  /** asset.metadata, so we can read iot.arcos_folder_id for the header line. */
  meta: Record<string, any>
}

/**
 * Live telemetry view for an asset linked to an Arcos folder.
 *
 * Polls `/api/v1/integrations/iot-snapshots/:assetId` every 10 seconds.
 * Each poll triggers one CMMS call → one Arcos `/iot/assets/:id/values`
 * call (the bulk endpoint), so traffic scales 1:1 with viewers × poll
 * frequency. 10s is the comfortable middle of the doc's recommended
 * 5–10s range — values feel live without doubling load.
 *
 * TanStack Query pauses this when the browser tab is hidden, and the
 * panel is unmounted when the user switches off the Telemetry tab, so
 * idle viewers stop costing anything immediately.
 *
 * Renders a grid of value tiles — one per point. A point is "stale" when
 * its `point_ts` is older than 5 minutes (yellow), or `quality === 'bad'`
 * (red). Both conditions are common enough during BACnet hiccups that we
 * want them visible at a glance.
 */
export function AssetTelemetryPanel({ assetId, meta }: Props) {
  const { formatDate } = useOrgSettings()
  const folderId = meta.iot?.source === 'arcos' ? (meta.iot?.arcos_folder_id as string | undefined) : undefined
  const linkedAt = meta.iot?.linked_at as string | undefined
  const isDeviceLinked = meta.iot?.source === 'device' && !!meta.iot?.device_imei

  const q = useQuery({
    queryKey: ['asset-iot', assetId],
    queryFn:  () => api.integrations.iotSnapshots.forAsset(assetId),
    refetchInterval: 10_000,
    staleTime: 5_000,
    enabled: !!folderId,
  })

  const snapshots = useMemo(
    () => Array.isArray(q.data) ? q.data : [],
    [q.data]
  )

  return (
    <Box sx={{ px: 3.5, py: 3 }}>
      {folderId && (
        <>
          {/* Header */}
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
            <Box sx={{
              width: 38, height: 38, borderRadius: 2,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: (t) => alpha(t.palette.primary.main, 0.12), color: 'primary.main',
            }}>
              <SensorsOutlinedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em' }}>
                Live telemetry
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                {`Arcos folder ${folderId.slice(0, 8)}… · linked ${linkedAt ? formatDate(linkedAt) : 'recently'}`}
              </Typography>
            </Box>
            <Tooltip title="Refresh now">
              <IconButton
                size="small"
                onClick={() => q.refetch()}
                disabled={q.isFetching}
              >
                {q.isFetching
                  ? <CircularProgress size={16} />
                  : <RefreshIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
          </Stack>

          {q.isLoading ? (
            <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress size={24} /></Box>
          ) : snapshots.length === 0 ? (
            <EmptyState />
          ) : (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 1.5,
              mb: 4,
            }}>
              {snapshots.map((s) => <PointTile key={s.arcos_point_id} snapshot={s} />)}
            </Box>
          )}
        </>
      )}

      <DeviceLinkSection assetId={assetId} meta={meta} />

      <SoftPointsSection assetId={assetId} deviceLinked={isDeviceLinked} />
    </Box>
  )
}

// ─── Device link ────────────────────────────────────────────────────────────
//
// Links this asset to a physical device by IMEI (e.g. an MPulse feedback
// terminal) — a human action, typed in here, unlike the Arcos folder
// link which Arcos itself writes via a machine call. Once linked, any
// soft point below can map a field from whatever payload that device
// posts to the device-events webhook (Settings → Integrations has the
// webhook URL + API key, and a log of every hit).

interface DeviceLinkFormState { imei: string; model: string }

function DeviceLinkSection({ assetId, meta }: { assetId: string; meta: Record<string, any> }) {
  const qc = useQueryClient()
  const confirmDlg = useConfirm()
  const { can } = usePermissions()
  const { formatDate } = useOrgSettings()
  const canEdit = can('assets:manage_telemetry')

  const iot = meta.iot?.source === 'device' ? meta.iot as {
    device_imei: string; device_model: string | null; linked_at?: string
  } : null

  const [dialogOpen, setDialogOpen] = useState(false)
  const [form, setForm] = useState<DeviceLinkFormState>({ imei: '', model: '' })

  function openDialog() {
    setForm({ imei: iot?.device_imei ?? '', model: iot?.device_model ?? '' })
    setDialogOpen(true)
  }

  const invalidate = () => qc.invalidateQueries({ queryKey: ['assets'] })

  const linkM = useMutation({
    mutationFn: () => api.integrations.device.link(assetId, {
      device_imei: form.imei.trim(), device_model: form.model.trim() || null,
    }),
    onSuccess: () => { toast.success(iot ? 'Device link updated' : 'Device linked'); setDialogOpen(false); invalidate() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to link device'),
  })

  const unlinkM = useMutation({
    mutationFn: () => api.integrations.device.unlink(assetId),
    onSuccess: () => { toast.success('Device unlinked'); invalidate() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to unlink device'),
  })

  // Nothing to link and nothing to show — don't take up space for
  // viewers who can't act on it either way.
  if (!iot && !canEdit) return null

  return (
    <Box sx={{ mb: 4 }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box sx={{
          width: 38, height: 38, borderRadius: 2,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: alpha('#0EA5E9', 0.12), color: '#0EA5E9',
        }}>
          <DevicesOtherOutlinedIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em' }}>
            Device
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            {iot
              ? `IMEI ${iot.device_imei}${iot.device_model ? ` · ${iot.device_model}` : ''} · linked ${iot.linked_at ? formatDate(iot.linked_at) : 'recently'}`
              : "No device linked — a device's readings can't reach this asset until one is"}
          </Typography>
        </Box>
        {canEdit && iot && (
          <Stack direction="row" spacing={0.5}>
            <Button size="small" onClick={openDialog} sx={{ fontSize: 12.5, fontWeight: 600, textTransform: 'none' }}>
              Edit
            </Button>
            <Button size="small" color="error" onClick={async () => {
              const ok = await confirmDlg({
                title: 'Unlink device?',
                message: 'Soft points mapped to this device will stop receiving new readings. Historical data is kept.',
                confirmLabel: 'Unlink', variant: 'danger',
              })
              if (ok) unlinkM.mutate()
            }} sx={{ fontSize: 12.5, fontWeight: 600, textTransform: 'none' }}>
              Unlink
            </Button>
          </Stack>
        )}
        {canEdit && !iot && (
          <Button size="small" variant="outlined" startIcon={<LinkOutlinedIcon sx={{ fontSize: 16 }} />} onClick={openDialog}
            sx={{ fontSize: 12.5, fontWeight: 600, textTransform: 'none', borderRadius: 1.5 }}>
            Link device
          </Button>
        )}
      </Stack>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>{iot ? 'Edit device link' : 'Link a device'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="IMEI" required autoFocus fullWidth size="small"
            value={form.imei} onChange={(e) => setForm((f) => ({ ...f, imei: e.target.value }))}
            placeholder="e.g. 869286032034600"
          />
          <TextField
            label="Model / label (optional)" fullWidth size="small"
            value={form.model} onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))}
            placeholder="e.g. MPulse Feedback Terminal"
          />
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
            Once linked, soft points below can map a field from this device's payload. The webhook URL
            and API key it should send to are on Settings → Integrations, which also logs every hit.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!form.imei.trim() || linkM.isPending} onClick={() => linkM.mutate()}>
            {linkM.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

// ─── Soft points ────────────────────────────────────────────────────────────

interface SoftPointFormState { name: string; unit: string; description: string; source_key: string; kind: 'telemetry' | 'feedback' }
const BLANK_FORM: SoftPointFormState = { name: '', unit: '', description: '', source_key: '', kind: 'telemetry' }

/** Recursively flattens a parsed JSON payload into {path, value} leaves,
 *  mirroring the backend's flattenPayloadPaths (device-events.service.js)
 *  so pasted-sample-payload discovery matches what the webhook actually
 *  resolves at ingest time. */
function flattenPayloadPaths(payload: unknown, prefix = ''): { path: string; value: unknown }[] {
  if (payload === null || typeof payload !== 'object') {
    return prefix ? [{ path: prefix, value: payload }] : []
  }
  const out: { path: string; value: unknown }[] = []
  if (Array.isArray(payload)) {
    payload.forEach((item, i) => {
      out.push(...flattenPayloadPaths(item, `${prefix}[${i}]`))
    })
  } else {
    for (const [key, val] of Object.entries(payload as Record<string, unknown>)) {
      const path = prefix ? `${prefix}.${key}` : key
      out.push(...flattenPayloadPaths(val, path))
    }
  }
  return out
}

function PayloadFieldPicker({ onPick }: { onPick: (path: string) => void }) {
  const [raw, setRaw] = useState('')
  const [error, setError] = useState<string | null>(null)
  const fields = useMemo(() => {
    if (!raw.trim()) return []
    try {
      const parsed = JSON.parse(raw)
      setError(null)
      return flattenPayloadPaths(parsed)
    } catch {
      setError('Not valid JSON')
      return []
    }
  }, [raw])

  return (
    <Box sx={{ mt: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Paste a sample device payload to discover field paths
      </Typography>
      <TextField
        multiline
        minRows={2}
        maxRows={6}
        fullWidth
        size="small"
        placeholder='{"I":"869286032034600","E":[{"Feed":2,"DT":"20-03-10 18:36:55","CustID":"0"}]}'
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        error={!!error}
        helperText={error ?? ' '}
      />
      {fields.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
          {fields.map((f) => (
            <Chip
              key={f.path}
              size="small"
              clickable
              label={`${f.path} = ${JSON.stringify(f.value)}`}
              onClick={() => onPick(f.path)}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

function SoftPointsSection({ assetId, deviceLinked }: { assetId: string; deviceLinked: boolean }) {
  const qc = useQueryClient()
  const confirmDlg = useConfirm()
  const { can } = usePermissions()
  const { formatDate } = useOrgSettings()
  const canEdit = can('assets:manage_telemetry')

  const q = useQuery({
    queryKey: ['asset-soft-points', assetId],
    queryFn: () => api.assets.listSoftPoints(assetId),
  })
  const points = useMemo(() => Array.isArray(q.data) ? q.data : [], [q.data])

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<AssetSoftPoint | null>(null)
  const [form, setForm] = useState<SoftPointFormState>(BLANK_FORM)

  function openCreate() { setEditing(null); setForm(BLANK_FORM); setDialogOpen(true) }
  function openEdit(p: AssetSoftPoint) {
    setEditing(p)
    setForm({ name: p.name, unit: p.unit ?? '', description: p.description ?? '', source_key: p.source_key ?? '', kind: p.kind })
    setDialogOpen(true)
  }

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['asset-soft-points', assetId] })
  }

  const saveM = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name.trim(),
        unit: form.unit.trim() || undefined,
        description: form.description.trim() || undefined,
        source_key: deviceLinked ? (form.source_key.trim() || undefined) : undefined,
        kind: form.kind,
      }
      return editing
        ? api.assets.updateSoftPoint(assetId, editing.id, body)
        : api.assets.createSoftPoint(assetId, body)
    },
    onSuccess: () => {
      toast.success(editing ? 'Soft point updated' : 'Soft point added')
      setDialogOpen(false)
      invalidate()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to save soft point'),
  })

  const deleteM = useMutation({
    mutationFn: (pointId: string) => api.assets.deleteSoftPoint(assetId, pointId),
    onSuccess: () => { toast.success('Soft point removed'); invalidate() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to remove soft point'),
  })

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <Box sx={{
          width: 38, height: 38, borderRadius: 2,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: alpha('#7C3AED', 0.12), color: '#7C3AED',
        }}>
          <TimelineOutlinedIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em' }}>
            Soft points
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            Manual meter readings recorded via checklists (e.g. kWh, Voltage) — not Arcos-fed
          </Typography>
        </Box>
        <Tooltip title="Refresh now">
          <IconButton
            size="small"
            onClick={() => q.refetch()}
            disabled={q.isFetching}
          >
            {q.isFetching
              ? <CircularProgress size={16} />
              : <RefreshIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Tooltip>
        {canEdit && (
          <Button size="small" startIcon={<AddCircleOutlineIcon sx={{ fontSize: 16 }} />} onClick={openCreate}
            sx={{ fontSize: 12.5, fontWeight: 600, textTransform: 'none', borderRadius: 1.5 }}>
            Add point
          </Button>
        )}
      </Stack>

      {q.isLoading ? (
        <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={22} /></Box>
      ) : points.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
          <TimelineOutlinedIcon sx={{ fontSize: 26, color: 'text.disabled', mb: 1 }} />
          <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>No soft points yet</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
            {canEdit ? 'Add one, then generate a telemetry checklist from it on the Checklists page.' : 'Ask an admin to add one.'}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
          {points.map((p) => (
            <Box key={p.id} sx={{
              p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2,
              display: 'flex', flexDirection: 'column', gap: 0.5, position: 'relative',
              transition: 'border-color 120ms ease',
              '&:hover': { borderColor: (t) => alpha(t.palette.primary.main, 0.5) },
              '&:hover .soft-point-actions': { opacity: 1 },
            }}>
              {canEdit && (
                <Stack direction="row" spacing={0.25} className="soft-point-actions"
                  sx={{ position: 'absolute', top: 6, right: 6, opacity: 0, transition: 'opacity 120ms ease' }}>
                  <IconButton size="small" onClick={() => openEdit(p)}>
                    <EditOutlinedIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                  <IconButton size="small" onClick={async () => {
                    const ok = await confirmDlg({ title: `Remove "${p.name}"?`, message: 'Historical readings are kept, but this point can no longer be selected for new telemetry checklists.', confirmLabel: 'Remove', variant: 'danger' })
                    if (ok) deleteM.mutate(p.id)
                  }}>
                    <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Stack>
              )}
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  {p.name}
                </Typography>
                {p.kind === 'feedback' && (
                  <Chip label="Feedback" size="small" sx={{
                    height: 16, fontSize: 9.5, fontWeight: 700,
                    bgcolor: alpha('#0D9488', 0.12), color: '#0D9488',
                    '& .MuiChip-label': { px: 0.6 },
                  }} />
                )}
              </Stack>
              <Stack direction="row" alignItems="baseline" spacing={0.75}>
                <Typography sx={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em' }}>
                  {p.last_value == null ? '—' : Number(p.last_value).toLocaleString()}
                </Typography>
                {p.unit && (
                  <Typography sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 600 }}>{p.unit}</Typography>
                )}
              </Stack>
              <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                {p.last_recorded_at ? `as of ${formatDate(p.last_recorded_at, true)}` : 'no readings yet'}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>{editing ? 'Edit soft point' : 'Add soft point'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Name" required autoFocus fullWidth size="small"
            value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Kwh, Voltage, Current"
          />
          <TextField
            label="Unit" fullWidth size="small"
            value={form.unit} onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
            placeholder="e.g. kWh, V, A"
          />
          <TextField
            label="Description" fullWidth size="small" multiline minRows={2}
            value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
          <TextField
            select label="Kind" fullWidth size="small"
            value={form.kind} onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value as 'telemetry' | 'feedback' }))}
            helperText={form.kind === 'feedback'
              ? 'Readings (1-5, 1 = best) feed the Feedback Overview dashboard instead of an asset metric.'
              : 'An ordinary metric like Voltage or Running Hours.'}
          >
            <MenuItem value="telemetry" sx={{ fontSize: 13 }}>Telemetry</MenuItem>
            <MenuItem value="feedback" sx={{ fontSize: 13 }}>Customer feedback</MenuItem>
          </TextField>
          {deviceLinked && (
            <>
              <TextField
                label="Payload key" fullWidth size="small"
                value={form.source_key} onChange={(e) => setForm((f) => ({ ...f, source_key: e.target.value }))}
                placeholder="e.g. E[0].Feed"
                helperText="Path into the device's JSON payload this point reads from. Use dot notation for objects and [N] for array indexes, e.g. E[0].Feed."
              />
              <PayloadFieldPicker onPick={(path) => setForm((f) => ({ ...f, source_key: path }))} />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!form.name.trim() || saveM.isPending}
            onClick={() => saveM.mutate()}
          >
            {saveM.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

// ─── Tile ───────────────────────────────────────────────────────────────────

const STALE_THRESHOLD_MS = 5 * 60 * 1000

function PointTile({ snapshot }: { snapshot: IotSnapshot }) {
  const now = Date.now()
  const tsMs = snapshot.point_ts ? new Date(snapshot.point_ts).getTime() : null
  const isStale = tsMs != null && now - tsMs > STALE_THRESHOLD_MS
  const isBad   = snapshot.quality === 'bad'

  const status = isBad ? 'bad' : isStale ? 'stale' : 'good'
  const dot = STATUS_DOT[status]

  return (
    <Box sx={{
      p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2,
      display: 'flex', flexDirection: 'column', gap: 0.5,
      transition: 'border-color 120ms ease',
      '&:hover': { borderColor: (t) => alpha(t.palette.primary.main, 0.5) },
    }}>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dot.color }} />
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {snapshot.point_name ?? snapshot.arcos_point_id}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="baseline" spacing={0.75}>
        <Typography sx={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em' }}>
          {formatValue(snapshot.value)}
        </Typography>
        {snapshot.unit && (
          <Typography sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 600 }}>
            {snapshot.unit}
          </Typography>
        )}
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.25 }}>
        <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
          {snapshot.point_ts
            ? `as of ${new Date(snapshot.point_ts).toLocaleTimeString()}`
            : 'no timestamp'}
        </Typography>
        {isBad && (
          <Chip size="small" label="Bad quality" sx={{ height: 18, fontSize: 10, bgcolor: '#FEF2F2', color: '#DC2626' }} />
        )}
        {!isBad && isStale && (
          <Chip size="small" label="Stale" sx={{ height: 18, fontSize: 10, bgcolor: '#FFFBEB', color: '#D97706' }} />
        )}
      </Stack>
    </Box>
  )
}

const STATUS_DOT = {
  good:  { color: '#10B981' },
  stale: { color: '#F59E0B' },
  bad:   { color: '#EF4444' },
} as const

function formatValue(v: IotSnapshot['value']): string {
  if (v == null) return '—'
  if (typeof v === 'boolean') return v ? 'On' : 'Off'
  if (typeof v === 'number') {
    // Compact display: 1234.56 → "1,234.56"; 0.0001 → "0.0001"
    if (Number.isInteger(v)) return v.toLocaleString()
    return Number(v.toFixed(3)).toLocaleString()
  }
  return String(v)
}

function EmptyState() {
  return (
    <Box sx={{
      py: 8, textAlign: 'center', border: '1px dashed',
      borderColor: 'divider', borderRadius: 2,
    }}>
      <SensorsOutlinedIcon sx={{ fontSize: 28, color: 'text.disabled', mb: 1 }} />
      <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
        Waiting for first snapshot from Arcos…
      </Typography>
      <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
        Telemetry usually appears within 30 seconds of a point being linked.
      </Typography>
    </Box>
  )
}
